import React, { useMemo, useState } from 'react'

const NON_SORTABLE_KEYS = new Set(['actions'])

function compareValues(a, b) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1

  const na = typeof a === 'number' ? a : Number(a)
  const nb = typeof b === 'number' ? b : Number(b)
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return na - nb
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/** Lightweight searchable Bootstrap table for Manage lists. */
function ManageTable({
  columns,
  rows,
  searchKeys = [],
  emptyMessage = 'No records found.',
  footer = null,
  hideSearch = false,
}) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows || []
    const keys =
      searchKeys.length > 0
        ? searchKeys
        : columns.map((c) => c.key).filter(Boolean)
    return (rows || []).filter((row) =>
      keys.some((key) => String(row[key] ?? '').toLowerCase().includes(q))
    )
  }, [rows, search, searchKeys, columns])

  const sorted = useMemo(() => {
    if (!sortKey || NON_SORTABLE_KEYS.has(sortKey)) return filtered
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => dir * compareValues(a[sortKey], b[sortKey]))
  }, [filtered, sortKey, sortDir])

  const handleSort = (key) => {
    if (!key || NON_SORTABLE_KEYS.has(key)) return
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const footerNode = typeof footer === 'function' ? footer(sorted) : footer

  return (
    <div>
      {hideSearch ? null : (
        <div className="mb-3 d-flex justify-content-end">
          <input
            type="search"
            className="form-control"
            style={{ maxWidth: 280 }}
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle manage-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const sortable = col.key && !NON_SORTABLE_KEYS.has(col.key)
                const active = sortable && sortKey === col.key
                const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                return (
                  <th
                    key={col.key || col.label}
                    style={col.width ? { width: col.width } : undefined}
                    className={sortable ? 'manage-th-sortable' : undefined}
                    onClick={sortable ? () => handleSort(col.key) : undefined}
                    aria-sort={sortable ? ariaSort : undefined}
                    scope="col"
                  >
                    <span className="manage-th-label">
                      {col.label}
                      {sortable && (
                        <span className={`manage-sort-ind${active ? ' is-active' : ''}`} aria-hidden="true">
                          {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
                        </span>
                      )}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sorted.map((row, i) => (
                <tr
                  key={
                    row.id ??
                    row.page_id ??
                    row.payment_id ??
                    row.donation_type_id ??
                    row.expense_id ??
                    row.expense_type_id ??
                    row.gallary_id ??
                    row.banner_id ??
                    i
                  }
                >
                  {columns.map((col) => (
                    <td key={col.key || col.label}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {footerNode && sorted.length > 0 ? <tfoot>{footerNode}</tfoot> : null}
        </table>
      </div>
      <div className="text-muted small">{sorted.length} record(s)</div>
    </div>
  )
}

export default ManageTable
